// Chakra imports
import { Box, SimpleGrid, useToast } from "components-lib";
import React, { useState, useEffect } from "react";
import ComplexTable from "views/admin/default/components/ComplexTable";
import { billscolumnsDataComplex } from "views/admin/default/variables/columnsData";
import { axiosInstance } from "config/api";
import { SocketProvider } from "context/SocketProvider";
import { useGlobalFilters } from "contexts/GlobalFiltersContext";
import useBills from "hooks/useBills";

export default function BillsPage({ onStatsUpdate }) {
  const [billsData, setBillsData] = useState([]);
  // Note: date/clinic/doctor come from GlobalFiltersContext (gFrom/gTo/gClinic/gDoctor/gDoctorIds).
  // Keep minimal local UI state here; global filters drive the BillsPage behavior.
  // (Other local UI state like searchQuery/filterType exists elsewhere in this file.)

  // parse user from localStorage, fallback to the above
  const getStoredUser = () => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      return parsed;
    } catch (e) {
      console.warn(
        "Failed to parse user from localStorage, using fallback:",
        e,
      );
      return;
    }
  };

  const user = getStoredUser();
  // helper to determine if profile represents an owner
  const isOwnerProfile = (p) => {
    if (!p) return false;
    const ric = (p.ric || "").toString().toLowerCase();
    const role = (p.role_in_clinic || p.role || "").toString().toLowerCase();
    return ric === "owner" || role === "owner";
  };

  // helper to determine if profile represents an admin (do not set any defaults)
  const isAdminProfile = (p) => {
    if (!p) return false;
    const role = (p.role_in_clinic || p.role || "").toString().toLowerCase();
    return role === "admin";
  };

  // derive initial IDs: clinic may be absent in object so fallback to empty string
  const getInitialClinicId = (p) => {
    if (!p) return "";
    return isOwnerProfile(p) ? (p?.clinic_id ?? p?.clinicId ?? "") : null;
  };
  const getInitialDoctorId = (p) => {
    if (!p) return "";
    return p.id ?? null;
    // return isOwnerProfile(p) ? null : (p.id ?? null);
  };
  const [defaultClinic, setdefaultClinic] = useState(() => {
    if (!user) return "";
    if (isAdminProfile(user)) return null;

    // if front desk, prefer user's clinic_id
    const role = (user.role_in_clinic || user.role || "")
      .toString()
      .toLowerCase();
    if (role === "front_desk") return user.clinic_id ?? user.clinicId ?? "";

    return getInitialClinicId(user);
  });
  const [defaultDoctor] = useState(() => {
    // const user = getStoredUser();
    if (isAdminProfile(user)) return null;
    return getInitialDoctorId(user);
  });
  const [defaultClinicName, setdefaultClinicName] = useState(user?.name || "");
  const [defaultDoctorName] = useState(user?.doctor || "");
  const [selectedClinic, setSelectedClinic] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("custom"); // 'custom', 'weekly', 'monthly'
  // const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const toast = useToast();

  // Global filters from central context
  const {
    filters: globalFilters,
    lists,
    actions: globalActions,
  } = useGlobalFilters();
  // Aliases used throughout this file to avoid undefined references
  const gClinic = globalFilters?.clinicId ?? null;
  const gDoctor = globalFilters?.doctorId ?? null;
  // `doctorIdsEffective` is the authoritative set of doctor IDs the user can see/select
  // expose it here as gDoctorIds for older code compatibility (memoized to avoid changing identity)
  const gDoctorIds = React.useMemo(
    () => globalFilters?.doctorIdsEffective ?? [],
    [globalFilters?.doctorIdsEffective],
  );
  const gFrom = globalFilters?.fromDate ?? null;
  const gTo = globalFilters?.toDate ?? null;

  // Use the centralized bills hook to fetch + filter + compute stats
  const {
    bills: hookBills,
    loading: billsLoading,
    error: billsError,
    stats: billsStats,
  } = useBills({
    clinicId: gClinic,
    doctorId: gDoctor,
    doctorIds: gDoctorIds,
    fromDate: gFrom,
    toDate: gTo,
    searchQuery,
    autoFetch: true,
  });

  // Sync hook output into local state/table setter so existing table wiring remains unchanged
  useEffect(() => {
    setBillsData(hookBills || []);
  }, [hookBills]);

  // BillsPage reads filter values (clinic/doctor/from/to + effective doctor list) directly
  // from GlobalFiltersContext. There is no local sync effect required here.
  // Changes to these global filter values are handled by the effect that loads bills
  // (the useEffect later in this file depends on the relevant global primitives).

  // Clinic and doctor lists are provided by GlobalFiltersContext (lists).
  // No local fetch is required here — the global filter provider already loads them.
  // This keeps BillsPage simple and ensures it follows the global filter state.

  // Set date range based on filter type
  // Adjust global date range using central actions (keeps behavior consistent with the top bar)
  const setDateRange = (type) => {
    const today = new Date();
    let startDate = new Date();

    if (type === "weekly") {
      startDate.setDate(today.getDate() - 7);
    } else if (type === "monthly") {
      startDate.setMonth(today.getMonth() - 1);
    }

    const from = startDate.toISOString().split("T")[0];
    const to = today.toISOString().split("T")[0];

    if (globalActions && typeof globalActions.setFromDate === "function") {
      globalActions.setFromDate(from);
    }
    if (globalActions && typeof globalActions.setToDate === "function") {
      globalActions.setToDate(to);
    }
    setFilterType(type);
  };

  // All fetching/filtering/stats logic is handled by `useBills` hook above. We keep local utility
  // functions only where they are still referenced elsewhere in this file.

  const handleFromDateChange = (e) => {
    const v = e.target.value;
    // Use global action to update central filters (no local fromDate used in this page)
    if (globalActions && typeof globalActions.setFromDate === "function") {
      globalActions.setFromDate(v);
    }
  };

  const handleToDateChange = (e) => {
    const v = e.target.value;
    // Use global action to update central filters (no local toDate used in this page)
    if (globalActions && typeof globalActions.setToDate === "function") {
      globalActions.setToDate(v);
    }
  };
  // Calculate total revenue and counts using robust fields:
  // - payment_action (primary), fall back to payment_status or appointment_status
  // - payment_receipt / payment_receipt_urls presence should count as a paid indicator
  const totalRevenue = (billsData || []).reduce((sum, bill) => {
    const receiptPresent = !!(
      bill.payment_receipt ||
      bill.payment_receipt_urls ||
      bill.payment_receipt_url
    );
    const action = String(
      bill.payment_action ||
        bill.payment_status ||
        bill.appointment_status ||
        "",
    ).toLowerCase();
    const isPaid = receiptPresent || action === "paid";
    const amount = isPaid
      ? parseFloat(
          bill.total_amt || bill.bill_amount || bill.total_amount || 0,
        ) || 0
      : 0;
    return sum + amount;
  }, 0);

  const paidBills = (billsData || []).filter((bill) => {
    const action = String(
      bill.payment_action ||
        bill.payment_status ||
        bill.appointment_status ||
        "",
    ).toLowerCase();
    const receiptPresent = !!(
      bill.payment_receipt ||
      bill.payment_receipt_urls ||
      bill.payment_receipt_url
    );
    return action === "paid" || receiptPresent;
  }).length;

  const pendingBills = (billsData || []).filter((bill) => {
    const action = String(
      bill.payment_action ||
        bill.payment_status ||
        bill.appointment_status ||
        "",
    ).toLowerCase();
    return action === "pending";
  }).length;

  const bookedBills = (billsData || []).filter((bill) => {
    const action = String(
      bill.payment_action ||
        bill.payment_status ||
        bill.appointment_status ||
        "",
    ).toLowerCase();
    // Treat either explicit 'booked' action or appointment_status 'BOOKED' as booked
    return action === "booked" || action === "booked";
  }).length;

  // Emit stats from the hook so the global header / parent can render updated numbers
  React.useEffect(() => {
    if (typeof onStatsUpdate === "function" && billsStats) {
      try {
        onStatsUpdate(billsStats);
      } catch (err) {
        console.warn("onStatsUpdate handler threw:", err);
      }
    }
  }, [billsStats, onStatsUpdate]);

  return (
    <SocketProvider>
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        {/* Header UI removed - global filters (GlobalFiltersBar) provide clinic/doctor/date selection and display billing stats.
            BillsPage now only renders the table; billing stats are computed and emitted via onStatsUpdate. */}
        <SimpleGrid columns={{ base: 1, md: 1, xl: 1 }} gap="20px" mb="20px">
          <ComplexTable
            columnsData={billscolumnsDataComplex}
            tableData={billsData}
            setTableDataComplex={setBillsData}
          />
        </SimpleGrid>
      </Box>
    </SocketProvider>
  );
}
